import React, { useMemo } from 'react';
import { Box, Flex, styled, TextV2 } from '@withjoy/joykit';
import type { PriceValuePropAsset } from '../../../propValues.types';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { galleryDesktopAspect, galleryMobileAspect, priceValuePropBackground } from '@apps/card/components/CardsConfig';
import { useStationeryTemplateProductPrices } from '../hooks/useStationeryTemplateProductPrices';
import { useGalleryConciergeProducts } from '../../../useGalleryConciergeProducts';
import { useCardDiscountContext } from '@apps/card/contexts/useCardDiscountContext';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { getConciergeSuiteUnitPrice, isConciergeServiceCategory } from '@apps/card/concierge/conciergeConfig';
import { useFormatPriceString } from '@apps/card/hooks/useFormattedPriceString';
import type { ConciergeServiceCategory } from '@apps/card/concierge/conciergeConfig';
import { useTranslation } from '@shared/core';
import { isBasicDigitalV2 } from '@apps/card/components/ProductUIConfig/isBasicDigitalV2';
import { isDigitalProduct } from '@apps/card/components/ProductUIConfig/isDigitalProduct';

type PriceValuePropCardProps = {
  data: PriceValuePropAsset & { type: 'priceValueProp' };
};

/**
 * The tile is a size container so its type can follow the tile's own width. The desktop sizes
 * below were drawn for a ~284px tile (five columns, no rail); a filter rail or a narrow
 * breakpoint can shrink a tile to ~170px, where a fixed 44px headline wraps to five lines and
 * (in Safari) inflates the aspect-ratio box past its row. Each rule keeps its px value first as
 * the fallback for browsers without container units.
 */
const Tile = styled(Flex)`
  container-type: inline-size;
  min-width: 0;
  min-height: 0;
`;

const Headline = styled(TextV2)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 44px;
    font-size: clamp(24px, 15cqw, 44px);
  }
`;

const PriceLabel = styled(TextV2)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 15px;
    font-size: clamp(11px, 5.3cqw, 15px);
  }
`;

const PriceAmount = styled(TextV2)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 17px;
    font-size: clamp(13px, 6cqw, 17px);
  }
`;

const TileBody = styled(Flex)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-top: 50px;
    padding-top: clamp(24px, 17.6cqw, 50px);
    padding-left: 28px;
    padding-left: clamp(14px, 9.9cqw, 28px);
    padding-right: 28px;
    padding-right: clamp(14px, 9.9cqw, 28px);
  }
`;

const PromoCode = styled(TextV2)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: 38px;
    margin-top: clamp(16px, 13.4cqw, 38px);
    font-size: 15px;
    font-size: clamp(11px, 5.3cqw, 15px);
  }
`;

const PriceList = styled(Flex)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: 36px;
    margin-top: clamp(16px, 12.7cqw, 36px);
  }
`;

const PriceRow = ({ label, price, strikethroughPrice }: { label: string; price: string; strikethroughPrice?: string }) => (
  <Flex
    flexDirection="row"
    alignItems="flex-end"
    gap={{ _: '4px', md: '6px' }}
  >
    <Box>
      <PriceLabel
        as="span"
        fontSize={{ _: '11px', md: '15px' }}
        fontWeight="regular"
        lineHeight="1.4"
        color="white"
      >
        {label}
        {strikethroughPrice !== undefined ? ' ' : ''}
      </PriceLabel>
      {strikethroughPrice !== undefined && (
        <PriceLabel
          as="span"
          fontSize={{ _: '11px', md: '15px' }}
          fontWeight="regular"
          lineHeight="1.4"
          color="white"
          textDecoration="line-through"
        >
          {strikethroughPrice}
        </PriceLabel>
      )}
    </Box>
    <PriceAmount
      typographyVariant="hed2"
      fontSize={{ _: '13px', md: '17px' }}
      color="white"
    >
      {price}
      {strikethroughPrice !== undefined && '*'}
    </PriceAmount>
  </Flex>
);

export const PriceValuePropCard = ({ data }: PriceValuePropCardProps) => {
  const isMobile = useIsMobileScreen();
  const { tq } = useTranslation('stationery');

  const productPrices = useStationeryTemplateProductPrices(data.representativeTemplate);
  // The free Basic Digital V2 row is hidden: it would sit beside premium digital under the
  // same "Digital" label, and only appears when the representative template happens to carry it.
  // Digital before Paper — hook returns a fresh array each render so no memo needed
  const sortedProductPrices = productPrices
    .filter(p => !isBasicDigitalV2({ id: p.productId, productType: p.productType }))
    .sort((a, b) => {
      const isDigitalA = isDigitalProduct(a);
      const isDigitalB = isDigitalProduct(b);
      return isDigitalA === isDigitalB ? 0 : isDigitalA ? -1 : 1;
    });

  const { currentCategory } = useCardCategoryContext();
  // Headline noun tracks the category being browsed. Of the `stationeryTemplateCategory`
  // key sets this is the only one that is plural for every category *and* has a non-empty
  // `default` — `stationeryTemplateCategoryToTile` is singular for saveTheDate/invitation,
  // and `dashboardCategoryNav` defaults to an empty string.
  const getCategoryNoun = useStationeryTemplateCategoryTranslations('designsGalleryPdpBreadcrumbCategory');
  const categoryNoun = getCategoryNoun(currentCategory) || tq`Cards`;
  const { conciergeProducts } = useGalleryConciergeProducts();
  const cardDiscountContext = useCardDiscountContext();
  const formatPrice = useFormatPriceString();

  const conciergeRow = useMemo(() => {
    if (!isConciergeServiceCategory(currentCategory)) return null;
    const categoryProducts = conciergeProducts[currentCategory as ConciergeServiceCategory] ?? [];
    if (!categoryProducts.length) return null;

    // USD per-card prices at 100 cards. `useFormatPriceString` converts them with the picker's currency,
    // as it does for every other price on this card.
    const cheapestUnitPrice = Math.min(...categoryProducts.map(o => getConciergeSuiteUnitPrice(currentCategory as ConciergeServiceCategory, o.identifier, 100)));
    const discountResult = cardDiscountContext!.paperlust.getPrice(cheapestUnitPrice);

    const sale = 'currentSale' in discountResult ? discountResult.currentSale : undefined;
    const discountedUnitPrice = sale ? Math.round(cheapestUnitPrice * (1 - sale.discountPercentage)) : null;

    return {
      basePrice: formatPrice(cheapestUnitPrice),
      discountedPrice: discountedUnitPrice !== null ? formatPrice(discountedUnitPrice) : undefined,
      preDiscountPrice: discountedUnitPrice !== null ? formatPrice(cheapestUnitPrice) : undefined,
      promoCode: sale?.code,
    };
  }, [currentCategory, conciergeProducts, cardDiscountContext, formatPrice]);

  const promoCode = sortedProductPrices.find(p => p.discount)?.discount?.promoCode ?? conciergeRow?.promoCode;
  const hasAnyDiscount = sortedProductPrices.some(p => !!p.discount) || !!conciergeRow?.discountedPrice;

  return (
    <Tile
      gridArea="auto"
      aspectRatio={isMobile ? galleryMobileAspect : galleryDesktopAspect}
      background={priceValuePropBackground}
      width="100%"
      height="100%"
      overflow="hidden"
    >
      <TileBody
        flexDirection="column"
        width="100%"
        height="100%"
        paddingTop={{ _: 8, md: '50px' }}
        paddingX={{ _: 5, md: 7 }}
      >
        <Headline
          fontSize={{ _: '30px', md: '44px' }}
          fontWeight="semibold"
          lineHeight="1.2"
          letterSpacing="-0.015em"
          color="white"
        >
          {tq`${categoryNoun} for every budget.`}
        </Headline>

        <PriceList
          flexDirection="column"
          gap="4px"
          marginTop={{ _: 7, md: '36px' }}
        >
          {sortedProductPrices.map(product => {
            const hasDiscount = !!product.discount && product.preDiscountedPrice !== product.basePrice;
            return (
              <PriceRow
                key={product.productId}
                label={product.name}
                price={product.basePrice}
                strikethroughPrice={hasDiscount ? product.preDiscountedPrice : undefined}
              />
            );
          })}

          {conciergeRow && (
            <PriceRow
              label={tq`Concierge`}
              price={conciergeRow.discountedPrice ?? conciergeRow.basePrice}
              strikethroughPrice={conciergeRow.discountedPrice ? conciergeRow.preDiscountPrice : undefined}
            />
          )}
        </PriceList>

        {hasAnyDiscount && promoCode && (
          <PromoCode
            typographyVariant="hed1"
            fontSize={{ _: '11px', md: '15px' }}
            color="white"
            marginTop={{ _: 7, md: '38px' }}
          >
            {tq`*Use code: ${promoCode}`}
          </PromoCode>
        )}
      </TileBody>
    </Tile>
  );
};
